import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Clock, Users, Award, MapPin, Calendar, AlertTriangle, FileText, Fingerprint } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

export default function CaseDetail() {
  const { slug } = useParams();
  const { user } = useCustomerAuth() || {};
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/cases/${slug}`)
      .then((res) => setC(res.data))
      .catch(() => setError("Caso não encontrado"))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleBuy = async () => {
    if (!c || c.status !== "available") return;
    setBuying(true);
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        case_slug: c.slug,
        quantity: 1,
        origin_url: window.location.origin,
        customer_email: user?.email || null,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Erro ao iniciar a compra");
      setBuying(false);
    }
  };

  if (loading)
    return (
      <Layout>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-32">
          <p className="mono text-sm text-bone-muted tracking-[0.3em]">A CARREGAR DOSSIER...</p>
        </div>
      </Layout>
    );

  if (!c)
    return (
      <Layout>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-32">
          <p className="headline text-4xl mb-4">Caso não encontrado</p>
          <Link to="/casos" className="btn-ghost mt-6 inline-flex">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>
        </div>
      </Layout>
    );

  const isAvailable = c.status === "available";

  return (
    <Layout>
      {/* HERO */}
      <section data-testid="case-hero" className="relative pt-12 pb-16 border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${c.cover_image})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-50/60 via-ink-50/80 to-ink-50" aria-hidden />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-12">
          <Link to="/casos" className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.3em] text-bone-muted hover:text-white mb-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Todos os Casos
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="mono text-[11px] tracking-[0.35em] text-blood">{c.code}</span>
                <span className="mono text-[11px] tracking-[0.35em] text-bone-faded">//</span>
                <span className="mono text-[11px] tracking-[0.35em] text-bone-muted uppercase">
                  {isAvailable ? "CASO ABERTO" : "EM BREVE"}
                </span>
              </div>

              <h1 className="headline text-5xl md:text-7xl lg:text-8xl leading-[0.9]">{c.title}</h1>
              <p className="text-bone-dim text-lg md:text-xl max-w-2xl leading-relaxed">{c.subtitle}</p>

              <div className="flex flex-wrap gap-6 pt-4 mono text-[11px] tracking-[0.25em] text-bone-muted">
                <span className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-blood" /> {c.location}</span>
                <span className="inline-flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-blood" /> {c.year}</span>
                <span className="inline-flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-blood" /> {c.duration}</span>
                <span className="inline-flex items-center gap-2"><Users className="w-3.5 h-3.5 text-blood" /> {c.players}</span>
                <span className="inline-flex items-center gap-2"><Award className="w-3.5 h-3.5 text-blood" /> {c.difficulty}</span>
                {c.age_rating && (
                  <span className="inline-flex items-center gap-2 border border-white/15 px-2 py-0.5">{c.age_rating}</span>
                )}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="dossier p-7 backdrop-blur-sm bg-ink-50/90">
                <p className="section-tag mb-5">Encomendar dossier</p>
                <div className="flex items-end justify-between mb-6 pb-6 border-b border-white/10">
                  <span className="mono text-[11px] tracking-[0.3em] text-bone-faded">PREÇO</span>
                  <span className="headline text-5xl text-blood">{c.price.toFixed(2)}€</span>
                </div>
                <ul className="space-y-2 mono text-[11px] text-bone-muted tracking-[0.15em] mb-7">
                  <li>· Dossier digital em PDF</li>
                  <li>· {c.suspects?.length || 0} suspeitos · {c.document_count ? `+${c.document_count} documentos` : "Dossier completo"}</li>
                  {c.made_in && <li>· Feito em {c.made_in}</li>}
                  <li>· Acesso imediato após pagamento</li>
                </ul>
                {isAvailable ? (
                  <button
                    data-testid="buy-case-btn"
                    onClick={handleBuy}
                    disabled={buying}
                    className="btn-primary w-full justify-center disabled:opacity-60"
                  >
                    {buying ? "A redirecionar..." : (<>Comprar dossier <ShoppingBag className="w-4 h-4" /></>)}
                  </button>
                ) : (
                  <button disabled className="btn-ghost w-full justify-center opacity-70 cursor-not-allowed">
                    {c.release_date || "Em breve"}
                  </button>
                )}
                {error && <p className="mt-4 text-xs text-blood mono tracking-[0.2em]">{error}</p>}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* SUMMARY */}
      {c.summary && (
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="section-tag mb-4">Resumo do caso</p>
              <h2 className="headline text-4xl md:text-5xl">{c.summary}</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-bone-dim leading-relaxed text-base md:text-lg">{c.full_description}</p>
            </div>
          </div>
        </section>
      )}
      {!c.summary && c.full_description && (
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 max-w-4xl">
            <p className="section-tag mb-6">Resumo do caso</p>
            <p className="text-bone-dim leading-relaxed text-base md:text-lg whitespace-pre-line">{c.full_description}</p>
          </div>
        </section>
      )}

      {/* VICTIM */}
      {c.victim && (
        <section data-testid="case-victim" className="py-20 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <p className="section-tag mb-4">Ficha da vítima</p>
            <h2 className="headline text-4xl md:text-6xl mb-12">Quem desaparece esta noite.</h2>
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7">
                {c.victim.image_url ? (
                  <div className="dossier p-3 md:p-4 bg-ink-100 relative">
                    <img
                      src={c.victim.image_url}
                      alt={c.victim.name}
                      className="w-full h-auto object-cover"
                    />
                    <span className="absolute top-6 right-6 stamp text-[10px] z-10">Vítima</span>
                  </div>
                ) : (
                  <div className="dossier p-2">
                    <div className="aspect-[4/5] bg-ink-200 flex items-center justify-center">
                      <div className="text-center px-6">
                        <Fingerprint className="w-20 h-20 text-white/10 mx-auto mb-4" />
                        <p className="mono text-[10px] tracking-[0.3em] text-bone-faded">FOTOGRAFIA NÃO DIVULGADA</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="lg:col-span-5 lg:sticky lg:top-32">
                <div className="dossier p-8 md:p-10">
                  <p className="mono text-[10px] tracking-[0.3em] text-blood mb-4">VÍTIMA DE HOMICÍDIO</p>
                  <h3 className="headline text-4xl md:text-5xl mb-3 leading-[0.95]">{c.victim.name}</h3>
                  <p className="mono text-[11px] text-bone-muted tracking-[0.25em] uppercase mb-6">
                    {c.victim.age && `${c.victim.age} anos`}
                    {c.victim.occupation && ` · ${c.victim.occupation}`}
                  </p>
                  {c.victim.organization && (
                    <p className="text-bone-dim mb-6 italic text-sm">{c.victim.organization}</p>
                  )}
                  <p className="text-bone-dim leading-relaxed text-sm md:text-base mb-8">{c.victim.description}</p>
                  <div className="grid grid-cols-2 gap-px bg-white/10">
                    <div className="bg-ink-200 p-4">
                      <p className="mono text-[10px] text-bone-faded tracking-[0.3em] mb-1">LOCAL</p>
                      <p className="text-sm text-white">Biblioteca</p>
                    </div>
                    <div className="bg-ink-200 p-4">
                      <p className="mono text-[10px] text-bone-faded tracking-[0.3em] mb-1">ESTADO</p>
                      <p className="text-sm text-blood">Caso aberto</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SUSPECTS */}
      {c.suspects?.length > 0 && (
        <section data-testid="case-suspects" className="py-20 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <p className="section-tag mb-4">Suspeitos</p>
                <h2 className="headline text-4xl md:text-6xl">Quem teve oportunidade?</h2>
              </div>
              <span className="mono text-[11px] text-bone-faded tracking-[0.3em]">
                {c.suspects.length.toString().padStart(2, "0")} IDENTIFICADOS
              </span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
              {c.suspects.map((s, i) => (
                <div key={i} className="bg-ink-100 p-7 group hover:bg-ink-200 transition-colors">
                  <div className="flex items-center gap-3 mb-5">
                    <Fingerprint className="w-5 h-5 text-blood" />
                    <p className="mono text-[10px] text-bone-faded tracking-[0.3em]">
                      SUSPEITO {String(i + 1).padStart(2, "0")}
                    </p>
                  </div>
                  <h3 className="headline text-2xl mb-2">{s.name}</h3>
                  {s.age && <p className="mono text-[11px] text-bone-muted mb-1 tracking-[0.15em]">{s.age} anos</p>}
                  <p className="mono text-[11px] text-blood tracking-[0.2em] uppercase mb-4">{s.role}</p>
                  <p className="text-sm text-bone-dim leading-relaxed mb-5">{s.description}</p>
                  {(s.personality || s.motive) && (
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      {s.personality && (
                        <div>
                          <p className="mono text-[9px] text-bone-faded tracking-[0.3em] mb-1">PERSONALIDADE</p>
                          <p className="text-xs text-bone-dim italic">{s.personality}</p>
                        </div>
                      )}
                      {s.motive && (
                        <div>
                          <p className="mono text-[9px] text-bone-faded tracking-[0.3em] mb-1">MOTIVO POSSÍVEL</p>
                          <p className="text-xs text-bone-dim redacted">{s.motive}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHAT'S INSIDE — teaser */}
      <section data-testid="case-whats-inside" className="py-20 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="section-tag mb-4">O que recebes</p>
              <h2 className="headline text-4xl md:text-6xl mb-6">Dentro da caixa.</h2>
              <p className="text-bone-muted leading-relaxed mb-6">
                Um dossier digital em PDF com mais de {c.document_count || 25} documentos originais.
                Descarrega, imprime e joga. Cada folha é um detalhe. Cada detalhe é uma escolha.
              </p>
              {c.made_in && (
                <div className="inline-flex items-center gap-3 border border-white/15 px-4 py-2">
                  <span className="mono text-[10px] tracking-[0.3em] text-bone-faded">FEITO EM</span>
                  <span className="mono text-[10px] tracking-[0.3em] text-white">{c.made_in.toUpperCase()}</span>
                </div>
              )}
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10">
              {[
                { t: "Fichas de suspeitos", d: "Personalidade, motivo e relações com a vítima." },
                { t: "Fotografias da cena", d: "Imagens reais do local. Cada pormenor pode contar." },
                { t: "Relatórios forenses", d: "Autópsia, análises microscópicas, peritagem." },
                { t: "Depoimentos", d: "Transcrições oficiais dos interrogatórios." },
                { t: "Documentos digitais", d: "Acesso exclusivo a emails, registos e câmaras." },
                { t: "Mapa da mansão", d: "Planta detalhada e passagens secretas." },
              ].map((it) => (
                <div key={it.t} className="bg-ink-100 p-6">
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-blood mt-1 shrink-0" />
                    <div>
                      <h4 className="headline text-lg mb-1.5">{it.t}</h4>
                      <p className="text-xs text-bone-muted leading-relaxed">{it.d}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EVIDENCE */}
      {c.evidence?.length > 0 && (
        <section data-testid="case-evidence" className="py-20 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="mb-12">
              <p className="section-tag mb-4">Provas materiais</p>
              <h2 className="headline text-4xl md:text-6xl">A cena fala. Sabes ouvir?</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
              {c.evidence.map((e, i) => (
                <div key={i} className="bg-ink-100 p-7">
                  <div className="flex items-center justify-between mb-5">
                    <span className="mono text-[10px] tracking-[0.3em] text-bone-faded">
                      PROVA #{String(i + 1).padStart(3, "0")}
                    </span>
                    <span className="mono text-[10px] tracking-[0.2em] text-blood uppercase border border-blood/40 px-2.5 py-1">
                      {e.type}
                    </span>
                  </div>
                  <h3 className="headline text-xl mb-3 leading-tight flex items-start gap-3">
                    <FileText className="w-4 h-4 text-blood mt-1.5 shrink-0" />
                    {e.title}
                  </h3>
                  <p className="text-sm text-bone-dim leading-relaxed pl-7">{e.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CLUES */}
      {c.clues?.length > 0 && (
        <section data-testid="case-clues" className="py-20 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="mb-12">
              <p className="section-tag mb-4">Pistas iniciais</p>
              <h2 className="headline text-4xl md:text-6xl">Onde começa a investigação.</h2>
            </div>
            <div className="space-y-px bg-white/10">
              {c.clues.map((cl, i) => (
                <div key={i} className="bg-ink-100 p-8 flex items-start gap-6">
                  <span className="headline text-4xl text-blood shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <h3 className="headline text-xl mb-2">{cl.title}</h3>
                    <p className="text-bone-dim leading-relaxed">
                      <span className="redacted">{cl.content}</span>
                    </p>
                  </div>
                  <AlertTriangle className="w-5 h-5 text-blood shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      {isAvailable && (
        <section className="py-24 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
            <h2 className="headline text-5xl md:text-7xl mb-6 leading-[0.95]">
              Resolves o caso <span className="text-blood">esta noite</span>?
            </h2>
            <p className="text-bone-muted max-w-xl mx-auto mb-8">
              O dossier chega selado. Tu decides quando o abres.
            </p>
            <button onClick={handleBuy} disabled={buying} data-testid="case-bottom-buy-btn" className="btn-primary mx-auto disabled:opacity-60">
              {buying ? "A redirecionar..." : `Encomendar — ${c.price.toFixed(2)}€`}
              <ShoppingBag className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}
    </Layout>
  );
}
